import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api-client";

type GenerateArchitectureRequestProps = {
  uuid: string;
  agent: string;
};

async function generateArchitectureRequest({
  uuid,
  agent,
  token,
}: GenerateArchitectureRequestProps & { token: string }): Promise<any> {
  const response = await apiFetch(
    `${config.isometricApiUrl}/architectural-graph/generate`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        uuid,
        agent,
      }),
    }
  );

  const res = await response.json();

  // Check if the response indicates an error
  if (!response.ok || res.error) {
    throw new Error(res.message || "Failed to generate architecture model.");
  }

  return res;
}

export default function useGenerateArchitectureService(
  onSettled?: (res: any | undefined, error: Error | null) => void
) {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: GenerateArchitectureRequestProps) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return generateArchitectureRequest({ ...params, token });
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch graph data
      queryClient.invalidateQueries({
        queryKey: ["neo4j-graph", variables.uuid],
      });
      toast.success(data?.message || "Architecture model generated successfully");
    },
    onError: (error) => {
      console.error("Error generating architecture:", error);
      toast.error(error.message || "Failed to generate architecture model");
    },
    onSettled,
  });
}
